import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import {
  obtenerPublicacionActiva,
  eliminarPublicacion,
  Publicacion,
} from "../../../../api/tableroInformativoService";
import { useAuth } from "../../../../context/AuthContext";
import Sidebar from "../../../../components/ui/sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Button } from "../../../../components/ui/button";
import {
  ArrowLeft,
  Calendar,
  PhoneCall,
  Trash2,
  Loader2,
  Image as ImageIcon,
  Building,
  MapPin,
} from "lucide-react";

const ALLOWED_ROLES = ["AD", "AS"];

export default function TableroDetailPage() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const { codigo = "", tipoCodigo = "" } = useParams<{ codigo: string; tipoCodigo: string }>();
  const location = useLocation();
  const unidadCodigo = session?.unidad?.id || "";
  const rolCodigo = (session?.rol?.id || "").toUpperCase();
  const isAuthorized = ALLOWED_ROLES.includes(rolCodigo);

  const [publicacion, setPublicacion] = useState<Publicacion | null>(
    (location.state as any)?.publicacion || null
  );
  const [isLoading, setIsLoading] = useState(!publicacion);
  const [isDeleting, setIsDeleting] = useState(false);
  const [imageError, setImageError] = useState(false);

  const fecha = publicacion?.tin_fecha || publicacion?.fecha_publicacion || "";
  const imagen = publicacion?.tin_ruta_foto || publicacion?.foto_base64 || publicacion?.ruta_foto || "";
  const whatsapp = useMemo(() => {
    const raw = publicacion?.tin_numero_celular || publicacion?.numero_whatsapp || "";
    // Remover todos los caracteres no numéricos
    let digits = raw.replace(/\D/g, "");
    // Si el número empieza con 0, removerlo
    if (digits.startsWith("0")) {
      digits = digits.substring(1);
    }
    // Si tiene 10 dígitos y empieza con 3, agregar código de país 57 para Colombia
    if (digits.length === 10 && digits.startsWith("3")) {
      digits = "57" + digits;
    }
    return digits;
  }, [publicacion?.tin_numero_celular, publicacion?.numero_whatsapp]);

  useEffect(() => {
    if (!isAuthorized) return;
    if (publicacion) return;
    if (!codigo || !tipoCodigo || !unidadCodigo) return;
    loadDetalle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codigo, tipoCodigo, unidadCodigo, isAuthorized]);

  const loadDetalle = async () => {
    try {
      setIsLoading(true);
      const payload = {
        codigo,
        unidad_residencial_codigo: unidadCodigo,
        tipo_publicacion_codigo: tipoCodigo,
      };
      const response = await obtenerPublicacionActiva(payload);
      if (response.code === "1" && response.object_ && response.object_.length > 0) {
        setPublicacion(response.object_[0]);
        setImageError(false); // Resetear error de imagen al cargar nueva publicación
      } else {
        throw new Error(response.message || "No se encontró la publicación");
      }
    } catch (error) {
      console.error("Error cargando detalle de publicación:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error instanceof Error ? error.message : "No se pudo cargar la publicación.",
        confirmButtonColor: "#3d5a40",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEliminar = async () => {
    const pubCodigo = publicacion?.tin_codigo || publicacion?.codigo || publicacion?.tab_codigo || codigo;
    if (!pubCodigo) {
      Swal.fire({
        icon: "warning",
        title: "Publicación inválida",
        text: "No se encontró el código de la publicación.",
        confirmButtonColor: "#3d5a40",
      });
      return;
    }

    const confirm = await Swal.fire({
      icon: "warning",
      title: "¿Eliminar publicación?",
      text: "Esta acción no se puede deshacer.",
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
      showCancelButton: true,
      confirmButtonColor: "#d33",
    });

    if (!confirm.isConfirmed) return;

    try {
      setIsDeleting(true);
      const id = pubCodigo.toString();
      const response = await eliminarPublicacion({ codigo: id });
      if (response.code === "1" || response.code === 1) {
        Swal.fire({
          icon: "success",
          title: "Publicación eliminada",
          confirmButtonColor: "#3d5a40",
          timer: 1800,
          timerProgressBar: true,
        });
        navigate(-1);
      } else {
        throw new Error(response.message || "No se pudo eliminar");
      }
    } catch (error) {
      console.error("Error eliminando publicación:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error instanceof Error ? error.message : "No se pudo eliminar la publicación.",
        confirmButtonColor: "#d33",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  if (!isAuthorized) {
    return (
      <div className="flex min-h-screen bg-gray-100 text-gray-900">
        <Sidebar />
        <main className="flex-1 p-4 sm:p-6 md:ml-64 transition-all">
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Acceso restringido</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">Esta sección está disponible solo para roles Administrador y Socio Administrador.</p>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-50 text-gray-900">
      <Sidebar />
      <main className="flex-1 md:ml-64 transition-all">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center gap-4 mb-4">
              <Button 
                variant="outline" 
                onClick={() => navigate(-1)}
                className="h-10 px-4 text-sm font-medium"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Volver
              </Button>
              <h1 className="text-2xl sm:text-3xl font-bold text-military-600">
                Detalle de publicación
              </h1>
              {(publicacion?.tin_tipo_nombre || publicacion?.tipo_publicacion_nombre) && (
                <span className="px-3 py-1 text-xs font-medium rounded-full bg-military-100 text-military-700 border border-military-200">
                  {publicacion.tin_tipo_nombre || publicacion.tipo_publicacion_nombre}
                </span>
              )}
            </div>
          </div>

          <Card className="border shadow-lg overflow-hidden">
            {isLoading ? (
              <div className="p-12 flex flex-col items-center justify-center gap-3 text-military-600">
                <Loader2 className="h-8 w-8 animate-spin" />
                <p className="text-base">Cargando publicación...</p>
              </div>
            ) : !publicacion ? (
              <div className="p-12 text-center text-gray-600">
                No se encontró la publicación solicitada.
              </div>
            ) : (
              <>
                {/* Imagen centrada */}
                <div className="w-full bg-gray-100">
                  {imagen && !imageError ? (
                    <div className="w-full flex items-center justify-center bg-white py-10 px-6 min-h-[400px]">
                      <img
                        src={imagen}
                        alt={publicacion.tin_titulo || publicacion.titulo || "Publicación"}
                        className="max-w-full max-h-[600px] w-auto h-auto object-contain"
                        loading="lazy"
                        onError={() => setImageError(true)}
                      />
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-64 text-gray-400">
                      <ImageIcon className="h-16 w-16" />
                    </div>
                  )}
                </div>

                {/* Contenido */}
                <div className="p-6 sm:p-8">
                  {/* Título */}
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-5">
                    {publicacion.tin_titulo || publicacion.titulo || "Sin título"}
                  </h2>

                  {/* Metadata */}
                  <div className="flex flex-wrap items-center gap-4 mb-6 pb-5 border-b border-gray-200">
                    {fecha && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar className="h-4 w-4 text-gray-500" />
                        <span>{fecha}</span>
                      </div>
                    )}
                    {(publicacion.tin_torre_nombre || publicacion.tin_apartamento_nombre) && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Building className="h-4 w-4 text-gray-500" />
                        <span>
                          {publicacion.tin_torre_nombre && publicacion.tin_apartamento_nombre
                            ? `Torre ${publicacion.tin_torre_nombre} - Apt ${publicacion.tin_apartamento_nombre}`
                            : publicacion.tin_torre_nombre
                            ? `Torre ${publicacion.tin_torre_nombre}`
                            : publicacion.tin_apartamento_nombre
                            ? `Apt ${publicacion.tin_apartamento_nombre}`
                            : ""}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Descripción */}
                  <div className="mb-6">
                    <h3 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-military-600" />
                      Descripción
                    </h3>
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap pl-6">
                      {publicacion.tin_descripcion || publicacion.descripcion || "Sin descripción"}
                    </p>
                  </div>

                  {/* Contacto */}
                  {whatsapp && (
                    <div className="mb-8">
                      <h3 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <PhoneCall className="h-4 w-4 text-green-600" />
                        Contacto
                      </h3>
                      <div className="pl-6">
                        <a
                          href={`https://wa.me/${whatsapp}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-green-600 hover:bg-green-700 text-white font-medium transition-colors shadow-sm"
                        >
                          <PhoneCall className="h-5 w-5" />
                          Contactar por WhatsApp
                        </a>
                      </div>
                    </div>
                  )}

                  {/* Botones */}
                  <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200">
                    <Button 
                      variant="outline"
                      onClick={() => navigate(-1)}
                      className="flex-1 h-11 text-base font-medium"
                    >
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Volver
                    </Button>
                    <Button
                      onClick={handleEliminar}
                      disabled={isDeleting}
                      className="flex-1 h-11 text-base font-medium bg-red-600 hover:bg-red-700 text-white border-0 shadow-sm"
                    >
                      {isDeleting ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Eliminando...
                        </>
                      ) : (
                        <>
                          <Trash2 className="h-4 w-4 mr-2" />
                          Eliminar publicación
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </Card>
        </div>
      </main>
    </div>
  );
}

